import axios from "axios";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

// (2026-10) Nota de cuenta por pagar al taller, con el formato nuevo de
// documento (el de la nota de remisión). La usan ListadoNotasCxP (botón PDF
// del renglón) y las entradas de proceso de Cortes y de Órdenes de Corte
// Externo (sale sola al guardar la entrada).

const ordenTallas = ["2XCH","XCH","EXTRA CHICA","CHICA","CH","M","MEDIANA","GRANDE","G","EXTRA GRANDE","XG","2XG","XXG","3XG","XXXG","4XG","XXXXG","5XG","XXXXXG","ESP","VARIOS","2","4","6","8","9","10","11","12","13","14","15","16","17","18","19","20","21","22","23","24","25","26","27","28","29","30","31","32","33","34","35","36","37","38","39","40","41","42","43","44","45","46","47","48","49","50","51","52","53","54","55","56"];

const txt = (v) => String(v ?? "").trim();
// "NA", "No" y vacío se tratan como sin dato
const dato = (v) => {
  const t = txt(v);
  return !t || ["NA", "NO"].includes(t.toUpperCase()) ? "" : t;
};
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const moneda = (n) =>
  "$" +
  new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));
const fechaCorta = (f) => {
  const [a, m, d] = txt(f).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : txt(f);
};

const traer = async (url) =>
  (
    await axios.get(url, {
      headers: {
        Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
      },
    })
  ).data;

// Trae la nota, su entrada de proceso y los renglones por talla, y saca el PDF.
export async function notaCxPPorId(idNota) {
  try {
    const nota = await traer(`${process.env.REACT_APP_URL_NOTAS_CXP}/${idNota}`);
    const pe = nota?.procesosEntrada?.[0];
    let entrada = null;
    let renglones = [];
    let empresa = "";
    if (pe?._id) {
      [entrada, renglones] = await Promise.all([
        traer(`${process.env.REACT_APP_URL_PROCESOS_ENTRADA}/${pe._id}`),
        traer(
          `${process.env.REACT_APP_URL_ARTICULOS_PROCESOS_ENTRADA}/proceso/${pe._id}`
        ),
      ]);
      // la empresa del pedido es la que le debe al taller
      const idEmpresa = pe.pedidos?.[0]?.empresas?.[0];
      if (idEmpresa) {
        try {
          const emp = await traer(
            `${process.env.REACT_APP_URL_EMPRESAS}/${idEmpresa._id || idEmpresa}`
          );
          empresa = txt(emp?.name);
        } catch (e) {
          // sin empresa se usa el nombre del sistema
        }
      }
    }
    PDFNotaCxP({
      nota,
      entrada,
      renglones: Array.isArray(renglones) ? renglones : [],
      empresa,
    });
  } catch (err) {
    console.error("Error generando la nota CxP:", err);
    Swal.fire("Error", "No se pudo generar la nota de CxP", "error");
  }
}

export function PDFNotaCxP({ nota, entrada, renglones, empresa }) {
  const pe = nota?.procesosEntrada?.[0] || {};
  const ped = pe.pedidos?.[0] || {};
  const art = entrada?.articulos?.[0] || pe.articulos?.[0] || {};

  const nombreEmpresa =
    txt(empresa) || txt(process.env.REACT_APP_NOMBRE_INSTITUCION);
  const taller = txt(nota?.proveedores?.[0]?.nombre_comercial);
  const cliente =
    txt(ped.clientes?.[0]?.nombre_comercial) +
    (dato(ped.subCliente) ? ` (${txt(ped.subCliente)})` : "");
  const modelo = [txt(art.codigo), txt(art.subDepartamentos?.[0]?.name)]
    .filter((t) => t)
    .join(" ");

  // ---- Renglones: color → talla con primeras / segundas / faltantes
  const posTalla = (t) => {
    const i = ordenTallas.indexOf(t);
    return i === -1 ? Infinity : i;
  };
  const porColor = {};
  const tallasSet = new Map(); // etiqueta → talla base (para ordenar)
  renglones.forEach((r) => {
    const color =
      txt(r.colores?.[0]?.name) ||
      txt(r.articulosProcesosSalida?.[0]?.colores?.[0]?.name) ||
      "Sin color";
    const tallaObj = r.tallas?.[0] || r.articulosProcesosSalida?.[0]?.tallas?.[0];
    const base = txt(tallaObj?.name) || "S/T";
    const variante = txt(tallaObj?.variantesTallas?.[0]?.name);
    const etiqueta = variante ? `${base} ${variante}` : base;
    tallasSet.set(etiqueta, base);
    if (!porColor[color]) porColor[color] = {};
    const c = (porColor[color][etiqueta] = porColor[color][etiqueta] || {
      entrada: 0,
      segundas: 0,
      faltantes: 0,
    });
    c.entrada += Number(r.cantidadEntrada || 0);
    c.segundas += Number(r.cantidadSegundas || 0);
    c.faltantes += Number(r.cantidadFaltantes || 0);
  });
  const tallas = [...tallasSet.entries()]
    .sort((a, b) => posTalla(a[1]) - posTalla(b[1]) || a[0].localeCompare(b[0]))
    .map(([etiqueta]) => etiqueta);

  const tot = { entrada: 0, segundas: 0, faltantes: 0 };
  Object.values(porColor).forEach((pt) =>
    Object.values(pt).forEach((c) => {
      tot.entrada += c.entrada;
      tot.segundas += c.segundas;
      tot.faltantes += c.faltantes;
    })
  );
  // nota sin renglones (p. ej. compra de muestras): se usan los totales guardados
  if (!renglones.length) {
    tot.entrada = Number(entrada?.totalPiezas ?? nota?.total_cantidad ?? 0);
    tot.segundas = Number(entrada?.totalPiezasSegundas || 0);
    tot.faltantes = Number(entrada?.totalPiezasFaltantes || 0);
  }

  // ---- Estilo (mismo que la nota de remisión)
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const ML = 15;
  const anchoUtil = pageW - ML * 2;
  const azul = [30, 58, 138];
  const azulClaro = [37, 99, 235];
  const fondoEtiqueta = [238, 244, 255];
  const borde = [169, 180, 200];
  const rojo = [220, 38, 38];
  const tinta = [30, 30, 30];
  const folio = nota?.idNotasCxP ?? "";

  // ---- Encabezado
  doc.setFont(undefined, "bold").setFontSize(17).setTextColor(...azul);
  doc.text(nombreEmpresa.toUpperCase(), ML, 20);
  doc.setFontSize(11.5).setTextColor(...azulClaro);
  doc.text("NOTA DE CUENTA POR PAGAR", ML, 26.5);
  doc.setFontSize(10.5).setTextColor(...azul);
  doc.text("FOLIO", pageW - ML, 19, { align: "right" });
  doc.setFontSize(16).setTextColor(...rojo);
  doc.text(String(folio), pageW - ML, 26.5, { align: "right" });
  doc.setTextColor(0).setFont(undefined, "normal");

  // ---- Cuadro de datos
  const etiqueta = (content) => ({
    content,
    styles: { fillColor: fondoEtiqueta, textColor: azul, fontStyle: "bold" },
  });
  const estiloCuadro = {
    fontSize: 9.5,
    cellPadding: 2,
    valign: "middle",
    lineColor: borde,
    lineWidth: 0.25,
    textColor: tinta,
  };
  const filasDatos = [
    [etiqueta("Taller:"), taller, etiqueta("Fecha:"), fechaCorta(nota?.fecha)],
    [
      etiqueta("Proceso:"),
      txt(entrada?.catalogoProcesos?.[0]?.name) || "—",
      etiqueta("Entrada #:"),
      txt(entrada?.idProcesosEntrada ?? pe.idProcesosEntrada),
    ],
    [
      etiqueta("Modelo:"),
      modelo || "—",
      etiqueta("Corte #:"),
      txt(entrada?.cortes?.[0]?.idCorte) || "—",
    ],
    [
      etiqueta("Cliente:"),
      cliente || "—",
      etiqueta("Pedido #:"),
      txt(ped.idPedido) || "—",
    ],
    [
      etiqueta("Salida #:"),
      txt(entrada?.procesosSalida?.[0]?.idProcesosSalida) || "—",
      etiqueta("Factura:"),
      dato(nota?.factura) || dato(entrada?.factura),
    ],
  ];
  doc.autoTable({
    startY: 30,
    margin: { left: ML, right: ML },
    tableWidth: anchoUtil,
    theme: "grid",
    styles: estiloCuadro,
    columnStyles: { 0: { cellWidth: 26 }, 1: { cellWidth: 64 }, 2: { cellWidth: 28 } },
    body: filasDatos,
  });

  // ---- Resumen de piezas. (2026-10) Langiani no maneja primeras / segundas
  // / faltantes: sólo las piezas que entregó el taller.
  const piezasDe = (c) => Number(c?.entrada || 0) + Number(c?.segundas || 0);
  doc.autoTable({
    startY: doc.autoTable.previous.finalY + 3,
    margin: { left: ML, right: ML },
    tableWidth: anchoUtil,
    theme: "grid",
    styles: { ...estiloCuadro, halign: "center", fontSize: 10 },
    body: [
      [
        etiqueta("PIEZAS"),
        { content: num(piezasDe(tot)), styles: { fontStyle: "bold" } },
      ],
    ],
  });

  // ---- Tabla por color y talla (un renglón por color)
  if (tallas.length) {
    const body = Object.keys(porColor).map((color) => {
      const pt = porColor[color];
      return [
        { content: color, styles: { fontStyle: "bold" } },
        ...tallas.map((t) => (pt[t] && piezasDe(pt[t]) ? num(piezasDe(pt[t])) : "")),
        { content: num(Object.values(pt).reduce((s, c) => s + piezasDe(c), 0)), styles: { fontStyle: "bold" } },
      ];
    });
    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 5,
      margin: { left: ML, right: ML, bottom: 22 },
      tableWidth: anchoUtil,
      theme: "grid",
      head: [["COLOR", ...tallas, "TOTAL"]],
      body,
      styles: {
        fontSize: 8.5,
        cellPadding: 1.4,
        valign: "middle",
        halign: "center",
        overflow: "linebreak",
        lineColor: borde,
        lineWidth: 0.2,
        textColor: tinta,
      },
      headStyles: { fillColor: azul, textColor: [255, 255, 255], fontStyle: "bold" },
      columnStyles: { 0: { cellWidth: 30 } },
    });
  }

  // ---- Observaciones + totales
  const abonado = Math.max(0, Number(nota?.total_costo || 0) - Number(nota?.saldo || 0));
  const filasTot = [
    ["PRECIO", moneda(entrada?.precio ?? pe.precio)],
    // (2026-10) huara: concepto extra por pieza, ya incluido en el subtotal
    ...(Number(nota?.importeHuara || 0) > 0
      ? [["HUARA", `${moneda(nota.precioHuara)} = ${moneda(nota.importeHuara)}`]]
      : []),
    ["SUB TOTAL", moneda(nota?.subtotal)],
    ["IVA", moneda(nota?.iva)],
    ["TOTAL", moneda(nota?.total_costo)],
    ...(abonado > 0.009 ? [["ABONADO", `-${moneda(abonado)}`]] : []),
    ["SALDO", moneda(nota?.saldo)],
  ];
  const anchoTot = 62;
  const xTot = pageW - ML - anchoTot;
  const obsLineas = doc
    .setFontSize(9.5)
    .splitTextToSize(dato(entrada?.observaciones), xTot - ML - 8);
  const altoObs = 13 + obsLineas.length * 4.5;
  const altoMin = Math.max(altoObs, 28);
  const altoFilaTot = Math.max(7.5, altoMin / filasTot.length);
  let y = doc.autoTable.previous.finalY + 5;
  if (y + altoFilaTot * filasTot.length + 45 > pageH - 22) {
    doc.addPage();
    y = 20;
  }
  doc.autoTable({
    startY: y,
    margin: { left: xTot, right: ML },
    tableWidth: anchoTot,
    theme: "grid",
    styles: { ...estiloCuadro, fontSize: 10, cellPadding: 2, minCellHeight: altoFilaTot },
    columnStyles: { 0: { cellWidth: 28 } },
    body: filasTot.map(([lbl, val]) => {
      const fuerte = lbl === "TOTAL" || lbl === "SALDO";
      return [
        etiqueta(lbl),
        {
          content: val,
          styles: {
            halign: "right",
            fontStyle: fuerte ? "bold" : "normal",
            textColor: fuerte ? azul : tinta,
          },
        },
      ];
    }),
  });
  const altoCaja = Math.max(doc.autoTable.previous.finalY - y, altoObs);
  doc.setDrawColor(...borde).setLineWidth(0.25);
  doc.rect(ML, y, anchoUtil, altoCaja);
  doc.line(xTot, y, xTot, y + altoCaja);
  doc.setFont(undefined, "bold").setFontSize(10).setTextColor(...azul);
  doc.text("Observaciones:", ML + 3, y + 6.5);
  doc.setFont(undefined, "normal").setFontSize(9.5).setTextColor(...tinta);
  if (obsLineas.length) doc.text(obsLineas, ML + 3, y + 12.5);

  // ---- Firmas
  const yFirma = y + altoCaja + 30;
  const anchoFirma = 68;
  const xFirma1 = ML + 8;
  const xFirma2 = pageW - ML - 8 - anchoFirma;
  doc.setDrawColor(...azul).setLineWidth(0.35);
  doc.line(xFirma1, yFirma, xFirma1 + anchoFirma, yFirma);
  doc.line(xFirma2, yFirma, xFirma2 + anchoFirma, yFirma);
  doc.setFont(undefined, "bold").setFontSize(10).setTextColor(...azul);
  doc.text("Entregó (Taller)", xFirma1 + anchoFirma / 2, yFirma + 5.5, {
    align: "center",
  });
  doc.text("Recibió (Nombre y firma)", xFirma2 + anchoFirma / 2, yFirma + 5.5, {
    align: "center",
  });

  // ---- Pie de página en todas las hojas
  const nPaginas = doc.internal.getNumberOfPages();
  for (let i = 1; i <= nPaginas; i++) {
    doc.setPage(i);
    doc.setFont(undefined, "normal").setFontSize(8.5).setTextColor(60, 60, 60);
    doc.text(`${nombreEmpresa.toUpperCase()} - Nota de CxP`, ML, pageH - 10);
    doc.text(`Folio: ${folio}`, pageW / 2, pageH - 10, { align: "center" });
    doc.text(`Página ${i} de ${nPaginas}`, pageW - ML, pageH - 10, {
      align: "right",
    });
  }
  doc.setTextColor(0);

  doc.save(`Nota CxP - Folio ${folio}.pdf`);
}
